import { Injectable } from '@angular/core';
import { iOSPermissions } from '@shared/constants/ios-permissions.enum';
import { PermissionTypes } from '@shared/constants/permission-types.enum';
import { StorageKeys } from '@shared/constants/storage.const';
import { IStoragePermission } from '@shared/interfaces/storage-permission.interface';
import { PlatformService } from '@core/services/system/platform/platform.service';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { LogService } from '../logger/log.service';
import { OPEN_SETTINGS } from '@shared/constants/open-settings.enum';
import { ANDROID_PERMISSION, PermissionState } from '@shared/constants';
import { IOSSettings, NativeSettings, requestCameraPermissions, OpenNativeSettings, AndroidPermissions } from '@core/capacitor-plugins/capacitor-plugins.class';
import { MyPosService } from '@core/services/system/devices/my-pos/my-pos.service';

/**
 * TODO(legacy-cordova): migrate off `cordova-plugin-android-permissions`.
 *
 * `AndroidPermissions` (via `@awesome-cordova-plugins/android-permissions`)
 * is the last consumer of this Cordova plugin. Capacitor 7 expects
 * permissions to be requested per-plugin (`Camera.requestPermissions()`,
 * `Geolocation.requestPermissions()`, `BluetoothLe.requestPermissions()` and
 * so on). The single string-based permission map below should be split into
 * per-feature calls. Keep behaviour intact until Android BLE/camera flows
 * are covered by integration tests.
 */
@Injectable()
export class PermissionService {
  private permissions = new Map<PermissionTypes, string[]>();
  private readonly logger = this.logService.createLogger('PermissionService');
  constructor(
    private androidPermissions: AndroidPermissions,
    private platformService: PlatformService,
    private localStorage: LocalStorage,
    private openNativeSettings: OpenNativeSettings,
    private logService: LogService,
    private myPosService: MyPosService
  ) {
    this.setAllPermitions();
  }

  private setAllPermitions() {
    const PERMISSION = { ...this.androidPermissions.PERMISSION, ...ANDROID_PERMISSION };
    this.permissions.set(PermissionTypes.CAMERA, [PERMISSION.CAMERA, iOSPermissions.CAMERA]);
    this.permissions.set(PermissionTypes.ACCESS_COARSE_LOCATION, [PERMISSION.ACCESS_COARSE_LOCATION, iOSPermissions.LOCATION]);
    this.permissions.set(PermissionTypes.BLUETOOTH_CONNECT, [PERMISSION.BLUETOOTH_CONNECT, iOSPermissions.BLUETOOTH_CONNECT]);
    this.permissions.set(PermissionTypes.BLUETOOTH_SCAN, [PERMISSION.BLUETOOTH_SCAN, iOSPermissions.BLUETOOTH_SCAN]);
  }

  async checkPermission(permissionTypes: PermissionTypes): Promise<boolean> {
    const permissions = this.permissions.get(permissionTypes);
    if (this.platformService.isWeb || !permissions) {
      return false;
    }
    const [androidPermission, iOSPermission] = permissions;
    if (this.platformService.isAndroid) {
      return this.checkAndroidPermission(androidPermission);
    }
    return this.checkiOSPermission(iOSPermission);
  }

  private checkAndroidPermission(permission: string) {
    return this.androidPermissions.checkPermission(permission).then((result) => result.hasPermission);
  }

  private async checkiOSPermission(permission: string): Promise<boolean> {
    switch (permission) {
      case iOSPermissions.CAMERA:
        return requestCameraPermissions().then((status) => status === PermissionState.granted);
      default:
        return true;
    }
  }

  async requestPermission(permissionTypes: PermissionTypes): Promise<boolean> {
    if (this.platformService.isWeb) {
      return true;
    }
    const permissions = this.permissions.get(permissionTypes);
    if (permissions && this.platformService.isAndroid) {
      const [androidPermission] = permissions;
      return this.androidPermissions.requestPermission(androidPermission).then((result) => result.hasPermission);
    }
    return false;
  }

  checkAndRequestPermission(permissionTypes: PermissionTypes): Promise<boolean> {
    return this.checkPermission(permissionTypes).then((hasPermission) => {
      if (!hasPermission) {
        return this.requestPermission(permissionTypes);
      }
      return hasPermission;
    });
  }

  async openSettings(setting: any, isNewActivity: boolean = false): Promise<any> {
    const isMyPosDevice = this.myPosService.isTargetDeviceMyPos;
    if (this.platformService.isWeb) {
      return { status: true };
    }

    if (this.platformService.isIOS) {
      return await NativeSettings.openIOS({
        option: setting === OPEN_SETTINGS.Bluetooth ? IOSSettings.Bluetooth : IOSSettings.App,
      });
    }

    if (isMyPosDevice) {
      return this.openNativeSettings.open([setting, isNewActivity]);
    }

    if (this.platformService.isAndroid) {
      return await NativeSettings.openAndroid({ option: setting });
    }

  }

  async checkLocationPermission(): Promise<boolean> {
    try {
      const hasPermission = await this.checkPermission(PermissionTypes.ACCESS_COARSE_LOCATION);
      const permission: IStoragePermission = this.localStorage.getObject(StorageKeys.permissions);
      permission.location = hasPermission;
      this.localStorage.setObject(StorageKeys.permissions, permission);
      return hasPermission;
    } catch (error) {
      this.logger.error(error, 'checkLocationPermission');
      throw error;
    }
  }
}
